import { useEffect, useRef } from "react";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { setChatSocketHandlers } from "../../../chatSocket";
import { markThreadMessagesRead, loadConversationsData } from "../../../chatApi";
import { ChatListScreen } from "./ChatListScreen";
import { ChatThreadScreen } from "./ChatThreadScreen";

// Top-level chat orchestrator, mounted once in AppRoot. Owns the bits that
// don't belong to either sub-screen specifically: the nav-bar unread dot,
// and wiring live WS push events (chatSocket.ts, dom.ts-free - connect/
// disconnect lifecycle is already owned by AuthGate.tsx since Phase 8).
// chatBridge.ts is gone as of Phase 10 - nav.ts (its only caller) was
// replaced by AppNav.tsx's plain react-router NavLink to "/chat".
export function ChatScreen() {
  const conversations = useAppState((s) => s.chatConversations);
  // Safety net for a lost typing_stopped push (tab closed, network drop) -
  // every typing_started resets this, so the indicator self-clears a few
  // seconds after the sender actually goes quiet even if the real "stopped"
  // event never arrives.
  const typingClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const hasUnread = conversations.some((c) => c.unreadCount > 0);
    getVanillaEl<HTMLSpanElement>("chatTabDot")?.classList.toggle("d-none", !hasUnread);
  }, [conversations]);

  useEffect(() => {
    setChatSocketHandlers({
      onNewMessage: (message) => {
        // Only append to the tail if the loaded window IS the live tail -
        // chatThreadHasMoreNewer true means the user has jumped to a search
        // result/date somewhere in the middle of history (see
        // ChatThreadScreen.tsx's jumpToMessage/jumpToDate), and stitching a
        // brand-new message onto content that isn't actually adjacent to
        // "now" would look wrong. loadConversationsData() below still keeps
        // the conversation list's preview/unread count correct either way -
        // nothing is silently lost, it just doesn't render here until the
        // user returns to the live view (leaving/re-entering the thread, or
        // exiting Search Mode, both reload the latest page fresh).
        if (appState.chatThreadId === message.conversationId && !appState.chatThreadHasMoreNewer) {
          appState.chatThreadMessages = [...appState.chatThreadMessages, message];
          notifyStateChange();
          markThreadMessagesRead(message.conversationId);
        }
        loadConversationsData();
      },
      onMessagesRead: (conversationId, messageIds, readAt) => {
        if (appState.chatThreadId !== conversationId) return;
        appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
          messageIds.includes(m.id) ? { ...m, readAt } : m,
        );
        notifyStateChange();
      },
      onMessageDeleted: (conversationId, messageId, deletedAt) => {
        if (appState.chatThreadId !== conversationId) {
          loadConversationsData();
          return;
        }
        appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
          m.id === messageId ? { ...m, text: null, attachment: null, deletedAt } : m,
        );
        notifyStateChange();
      },
      onMessageReaction: (conversationId, messageId, reactions) => {
        if (appState.chatThreadId !== conversationId) return;
        appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
          m.id === messageId ? { ...m, reactions } : m,
        );
        notifyStateChange();
      },
      onTypingStarted: (conversationId) => {
        if (appState.chatThreadId !== conversationId) return;
        appState.typingConversationId = conversationId;
        notifyStateChange();
        if (typingClearTimer.current) clearTimeout(typingClearTimer.current);
        typingClearTimer.current = setTimeout(() => {
          if (appState.typingConversationId === conversationId) {
            appState.typingConversationId = null;
            notifyStateChange();
          }
        }, 5000);
      },
      onTypingStopped: (conversationId) => {
        if (typingClearTimer.current) clearTimeout(typingClearTimer.current);
        if (appState.typingConversationId !== conversationId) return;
        appState.typingConversationId = null;
        notifyStateChange();
      },
      onMessageEdited: (conversationId, message) => {
        if (appState.chatThreadId !== conversationId) return;
        appState.chatThreadMessages = appState.chatThreadMessages.map((m) => (m.id === message.id ? message : m));
        notifyStateChange();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ChatListScreen />
      <ChatThreadScreen />
    </>
  );
}
